import _ from 'underscore';
import {
  IPerformanceLoggerInstrumentation, IPerformanceTimingOptions, IWindowWithPerformance, IPerformanceLogExtensions,
} from './types';

declare const window: IWindowWithPerformance;

/**
 * Returns a function that checks if the given object is of the specified type.
 * @param {string} type - The type to check against.
 * @returns {function} - A function that takes an object and returns true if it is of the specified type, false otherwise.
 */
const isType = (type: string) => (obj: unknown) => Object.prototype.toString.call(obj) === `[object ${type}]`;

/**
 * Checks if the given object is an array.
 *
 * @param {any} obj - The object to check.
 * @returns {boolean} - Returns true if the object is an array, else false.
 */
const isArray = (obj: unknown) => (Array.isArray(obj) || isType('Array')(obj));

/**
 * Checks if a given value is a function.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - Returns `true` if the value is a function, else `false`.
 */
const isFunction = isType('Function');

/**
 * Checks if a given value is a number.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - Returns `true` if the value is a number, else `false`.
 */
const isNumber = isType('Number');

/**
 * Create an instance of Performance Timing Logger
 */
export class PerformanceTimingLogger {
  inactiveTime: number | undefined;

  instrumentation: IPerformanceLoggerInstrumentation;

  isPageLoaded!: boolean;

  pageNavLogged!: boolean;

  timer!: any;

  updateInterval!: number;

  constructor(options: IPerformanceTimingOptions = {} as IPerformanceTimingOptions) {
    this.instrumentation = options.instrumentation;
    this.updateInterval = options.updateInterval || 2500;
  }

  /**
   * init performance timing logging
   * @returns {undefined} - no returns
   */
  init() {
    if (!isFunction(window.performance.getEntriesByType)) {
      return;
    }

    this.isPageLoaded = false;
    this.pageNavLogged = false;

    if (document.readyState === 'complete') {
      this.logOnLoad();
    } else {
      window.addEventListener('load', () => this.logOnLoad(), false);
    }

    this.timer = setInterval(() => this.logOnTimer(), this.updateInterval);

    this.instrumentation.beforeStop = () => {
      this.logOnUnload();
    };
  }

  /**
   * @private
   * @returns {undefined} - no returns
   */
  logOnLoad() {
    this.writeResourceTimingLogs();
    this.tryRecordInactiveTime();
    this.tryLogPageNavigation();
    this.isPageLoaded = true;
  }

  /**
   * @private
   * @returns {undefined} - no returns
   */
  logOnTimer() {
    this.writeResourceTimingLogs();
    this.tryLogPageNavigation();
  }

  /**
   * @private
   * @returns {undefined} - no returns
   */
  logOnUnload() {
    this.writeResourceTimingLogs();
  }

  /**
   * writes resource timing logs
   * @private
   * @returns {undefined} - no returns
   */
  writeResourceTimingLogs() {
    const logs = window.performance.getEntriesByType('resource');

    this.clearLogBuffer();

    if (!logs.length) {
      return;
    }

    logs.forEach((log) => {
      _.extend(log, { pageLoaded: this.isPageLoaded });
      this.instrumentation.writeRawLog(log);
    });
  }

  /**
   * If supported, record the amount of time the page was in the background.
   * It gets the foreground time from window.performance.activeTime
   * @private
   * @returns {undefined} - no returns
   */
  tryRecordInactiveTime() {
    if (isFunction(window.performance.activeTime)) {
      this.inactiveTime = window.performance.now() - window.performance.activeTime();
    }
  }

  /**
   * try log page navigation logs
   * @private
   * @returns {bool} - if page navigation has been logged
   */
  tryLogPageNavigation() {
    if (!this.pageNavLogged
      && window.performance.timing
      && window.performance.timing.loadEventEnd > 0) {
      this.pageNavLogged = this.writePageNavigationLogs();
    }

    return this.pageNavLogged;
  }

  /**
   * writes page navigation logs
   * @private
   * @returns {bool} - if page navigation has been logged
   */
  writePageNavigationLogs() {
    const extensions: IPerformanceLogExtensions = {
      isPageNavigationLog: true,
    };

    if (window.chrome && isFunction(window.chrome.loadTimes)) {
      const { firstPaintTime } = (window.chrome.loadTimes() || {});

      if (isNumber(firstPaintTime) && firstPaintTime! > 0) {
        extensions.firstPaintTime = firstPaintTime! * 1000;
      } else {
        return false;
      }
    }

    const navTimingV2Logs = window.performance.getEntriesByType('navigation');

    if (isArray(navTimingV2Logs) && navTimingV2Logs.length > 0) {
      const navTimingV2Log = navTimingV2Logs[0] as PerformanceNavigationTiming;
      const colsToReplace = {
        unloadEventStart: navTimingV2Log.unloadEventStart,
        unloadEventEnd: navTimingV2Log.unloadEventEnd,
        redirectCount: navTimingV2Log.redirectCount,
        redirectStart: navTimingV2Log.redirectStart,
        redirectEnd: navTimingV2Log.redirectEnd,
      };
      _.extend(extensions, colsToReplace);
    } else {
      extensions.redirectCount = window.performance.navigation.redirectCount;
    }

    if (this.inactiveTime) {
      extensions.inactiveTime = this.inactiveTime;
    }

    this.instrumentation.writeRawLog(_.extend({}, window.performance.timing, extensions));
    return true;
  }

  /**
   * @private
   * @returns {undefined} - no returns
   */
  clearLogBuffer() {
    if (isFunction(window.performance.clearResourceTimings)) {
      window.performance.clearResourceTimings();
    } else if (isFunction(window.performance.webkitClearResourceTimings)) {
      window.performance.webkitClearResourceTimings();
    }
  }
}
